import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import DateFilterField from 'components/DateFilterField';
import DialogHeader from 'components/DialogHeader';
import TeamSelectField from 'components/TeamSelectField';
import { useAppDispatch } from 'store';
import {
  AgendamentoStatusTypes,
  AgendamentoStatusValues,
} from 'store/reducers/agendamentos';
import {
  fetchCanaisSelect,
  SelectChannelInterface,
} from 'store/reducers/canais';
import { EquipeInterface, fetchEquipes } from 'store/reducers/equipes';
import { IFiltersOptions } from 'types';

import ChannelIdSelectField from './ChannelIdSelectField';
import StatusSelectField from './StatusSelectField';

export type ModalFilterOptionsType = {
  status: AgendamentoStatusTypes | '';
  date: MaterialUiPickersDate | null;
  teamId: string;
  channelCode: string;
  team?: EquipeInterface;
  canal?: SelectChannelInterface;
};

export const defaultModalFilters: ModalFilterOptionsType = {
  status: '',
  date: null,
  teamId: '',
  channelCode: '',
  team: undefined,
  canal: undefined,
};

export function mapModalFiltersToChips(
  options: ModalFilterOptionsType,
): IFiltersOptions[] {
  const results: IFiltersOptions[] = [];

  if (options.status) {
    results.push({
      property: 'status',
      value: options.status,
      labelProperty: 'Status',
      labelValue: AgendamentoStatusValues[options.status] || options.status,
    });
  }

  if (options.date) {
    results.push({
      property: 'date',
      value: options.date,
      labelProperty: 'Data',
      labelValue: options.date.toLocaleDateString(),
    });
  }

  if (options.teamId) {
    results.push({
      property: 'teamId',
      value: options.teamId,
      labelProperty: 'Equipe',
      labelValue: options.team?.nome || options.teamId,
    });
  }

  if (options.channelCode) {
    results.push({
      property: 'channelCode',
      value: options.channelCode,
      labelProperty: 'Canal',
      labelValue: options.canal?.name || options.channelCode,
    });
  }

  return results;
}

type ModalFiltersProps = {
  open: boolean;
  onClose: () => void;
  initialFilters: ModalFilterOptionsType;
  onApply: (filters: ModalFilterOptionsType, chips: IFiltersOptions[]) => void;
  onClear: () => void;
};

export default function ModalFilters(props: ModalFiltersProps) {
  const { open, onClose, initialFilters, onApply, onClear } = props;
  const dispatch = useAppDispatch();

  const [localFilters, setLocalFilters] =
    useState<ModalFilterOptionsType>(initialFilters);

  useEffect(() => {
    if (open) {
      setLocalFilters(initialFilters);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchCanaisSelect());
    dispatch(fetchEquipes());
  }, [dispatch]);

  const handleChange = (newFilters: Partial<ModalFilterOptionsType>) => {
    setLocalFilters(prev => ({ ...prev, ...newFilters }));
  };

  const hasAnyFilter = (filters: ModalFilterOptionsType): boolean => {
    return (
      !!filters.status ||
      !!filters.date ||
      !!filters.teamId ||
      !!filters.channelCode
    );
  };

  const handleApply = () => {
    if (!hasAnyFilter(localFilters)) return;

    onApply(localFilters, mapModalFiltersToChips(localFilters));
    onClose();
  };

  const handleClear = () => {
    if (!hasAnyFilter(localFilters)) {
      onClose();
      return;
    }
    onClear();
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open}>
      <DialogHeader title="Filtrar agendamentos" onClose={onClose} />

      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={16} paddingTop={1}>
          <StatusSelectField
            value={localFilters.status}
            onFilter={(status: AgendamentoStatusTypes) =>
              handleChange({ status })
            }
            onReset={() => handleChange({ status: '' })}
          />

          <DateFilterField
            value={localFilters.date}
            onFilter={d => handleChange({ date: d })}
            onReset={() => handleChange({ date: null })}
          />

          <TeamSelectField
            value={localFilters.teamId}
            onFilter={(equipe: EquipeInterface | null) => {
              handleChange({
                teamId: equipe?.id || undefined,
                team: equipe || undefined,
              });
            }}
            onReset={() => handleChange({ teamId: '', team: undefined })}
          />

          <ChannelIdSelectField
            value={localFilters.channelCode}
            onFilter={(canal: SelectChannelInterface | null) => {
              handleChange({
                channelCode: canal?.id || undefined,
                canal: canal || undefined,
              });
            }}
            onReset={() => handleChange({ channelCode: '', canal: undefined })}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear}>Limpar</Button>
        <Button variant="contained" color="primary" onClick={handleApply}>
          Aplicar filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
}
